import { t } from '@lingui/core/macro';
import { CostFromRuleDTO, EvaluatedTcoCostDTO, TcoCostDTO as TcoSolutionCostDTO } from '@luminovo/api-contracts';
import { assertUnreachable, isPresent, MonetaryValue } from '@luminovo/commons';
import { colorSystem, Flexbox, Text } from '@luminovo/design-system';
import { formatMonetaryValue, transEnum } from '@luminovo/frontend-utils';
import type { JSX } from 'react';

import { CostType, extractEvaluatedCostForType } from '../../extractors';
import { offerTcoCostTypeTranslations } from '../../i18n';
import { CostTag } from '../tags/AdditionalCostTag';

const extractCost = (evaluatedCost: EvaluatedTcoCostDTO): MonetaryValue | null => {
    return evaluatedCost.cost.preferred;
};

const extractCostName = ({
    cost,
    tcoRuleNameMap,
}: {
    cost: CostFromRuleDTO;
    tcoRuleNameMap: Record<string, string>;
}): string => {
    const { type, origin } = cost.origin;
    switch (type) {
        case 'Manual':
            return origin;
        case 'Automatic':
            return tcoRuleNameMap[origin] ?? t`Unkown rule`;
        case 'Offer':
            return transEnum(origin, offerTcoCostTypeTranslations);
        default:
            assertUnreachable(type);
    }
};

export const CostFromRule = ({
    cost,
    tcoRuleNameMap,
}: {
    cost: CostFromRuleDTO;
    tcoRuleNameMap: Record<string, string>;
}): JSX.Element => {
    const costName = extractCostName({ cost, tcoRuleNameMap });

    return (
        <Flexbox
            gap={8}
            sx={{
                width: '100%',
                justifyContent: 'space-between',
            }}
        >
            <Text variant="body-small" color={colorSystem.neutral[7]}>
                {costName}
            </Text>
            <Text variant="body-small" color={colorSystem.neutral[7]}>
                {formatMonetaryValue(cost.cost.preferred, 'unit-price')}
            </Text>
        </Flexbox>
    );
};

const CostRow = ({
    cost,
    costType,
    tcoRuleNameMap,
}: {
    cost: EvaluatedTcoCostDTO | null;
    costType: CostType;
    tcoRuleNameMap: Record<string, string>;
}): JSX.Element => {
    const costForRow = cost ? extractCost(cost) : undefined;

    return (
        <Flexbox
            gap={8}
            sx={{
                flexDirection: 'column',
            }}
        >
            <Flexbox
                sx={{
                    justifyContent: 'space-between',
                    minWidth: 200,
                    alignItems: 'center',
                }}
            >
                <CostTag costType={costType} />
                {isPresent(cost) ? `${formatMonetaryValue(costForRow, 'unit-price')} ${t`per unit`}` : '-'}
            </Flexbox>
            {isPresent(cost) && (
                <Flexbox
                    gap={8}
                    sx={{
                        flexDirection: 'column',
                    }}
                >
                    {cost.costs_from_rules.map((costFromRule) => (
                        <CostFromRule
                            key={costFromRule.origin.origin}
                            cost={costFromRule}
                            tcoRuleNameMap={tcoRuleNameMap}
                        />
                    ))}
                </Flexbox>
            )}
        </Flexbox>
    );
};

export const SimpleCostRow = ({ cost, costType }: { cost: MonetaryValue | null; costType: CostType }): JSX.Element => {
    return (
        <Flexbox
            sx={{
                justifyContent: 'space-between',
                minWidth: 200,
            }}
        >
            <CostTag costType={costType} />
            {isPresent(cost) ? `${formatMonetaryValue(cost)} ${t`per unit`}` : '-'}
        </Flexbox>
    );
};

export const TcoCostTooltip = ({
    tcoCost,
    tcoRuleNameMap,
}: {
    tcoCost: TcoSolutionCostDTO | undefined;
    tcoRuleNameMap: Record<string, string>;
}): JSX.Element => {
    return (
        <Flexbox
            gap={16}
            sx={{
                flexDirection: 'column',
                padding: 1,
            }}
        >
            <CostRow
                cost={extractEvaluatedCostForType('packaging', tcoCost)}
                costType="packaging"
                tcoRuleNameMap={tcoRuleNameMap}
            />
            <CostRow
                cost={extractEvaluatedCostForType('discount', tcoCost)}
                costType="discount"
                tcoRuleNameMap={tcoRuleNameMap}
            />
            <CostRow
                cost={extractEvaluatedCostForType('customs', tcoCost)}
                costType="customs"
                tcoRuleNameMap={tcoRuleNameMap}
            />
            <CostRow
                cost={extractEvaluatedCostForType('shipping', tcoCost)}
                costType="shipping"
                tcoRuleNameMap={tcoRuleNameMap}
            />
            <CostRow
                cost={extractEvaluatedCostForType('other', tcoCost)}
                costType="other"
                tcoRuleNameMap={tcoRuleNameMap}
            />
        </Flexbox>
    );
};
